import { Manager } from 'socket.io-client';
import { getSocketBaseUrl } from './socketUtils';

let manager = null;
const sockets = {};

/**
 * Get a singleton socket instance for a specific namespace.
 * Reuses the underlying Manager connection (multiplexing).
 * @param {string} namespace - The namespace to connect to (e.g., '/analysis')
 * @returns {Socket} The socket instance
 */
export const getSocket = (namespace) => {
  const baseUrl = getSocketBaseUrl();
  if (!baseUrl) return null;

  // Initialize Manager if not exists
  if (!manager) {
    console.log('[SocketManager] Initializing new Manager with baseUrl:', baseUrl);
    manager = new Manager(baseUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      withCredentials: true,
      autoConnect: true
    });
  }

  // Ensure namespace starts with /
  const nsp = namespace.startsWith('/') ? namespace : `/${namespace}`;

  // Create or retrieve socket for this namespace
  if (!sockets[nsp]) {
    console.log(`[SocketManager] Creating new socket for namespace: ${nsp}`);
    sockets[nsp] = manager.socket(nsp);
    
    // Add default error logging
    sockets[nsp].on('connect_error', (err) => {
      console.warn(`[SocketManager] Connection error for ${nsp}:`, err.message);
    });
  }

  return sockets[nsp];
};

/**
 * Disconnect a socket for a namespace (forcefully).
 * NOTE: Since sockets are shared, calling this will disconnect it for ALL consumers.
 * Use with caution. Generally prefer just removing listeners in components.
 * @param {string} namespace 
 */
export const disconnectSocket = (namespace) => {
  const nsp = namespace.startsWith('/') ? namespace : `/${namespace}`;
  if (sockets[nsp]) {
    console.log(`[SocketManager] Disconnecting socket for namespace: ${nsp}`);
    sockets[nsp].disconnect();
    delete sockets[nsp];
  }
};

/**
 * Disconnect all sockets and the manager.
 */
export const disconnectAll = () => {
  Object.keys(sockets).forEach(nsp => {
    sockets[nsp].disconnect();
  });
  if (manager) {
    manager._close(); // Accessing private _close to fully shut down? or just close()
    manager = null;
  }
};

// Default export for convenience
const socketManager = {
  getSocket,
  disconnectSocket,
  disconnectAll
};

export default socketManager;
